/**
 * WorkOfferForm - Formulário para criar oferta de trabalho (profissional)
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Criação de oferta de serviço
 * - Validação de limites do plano
 * - Campos: título, localização, salário esperado, disponibilidade, descrição, habilidades
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, Button, Input, Label, Textarea, Select (shadcn/ui)
 */

import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOfferForm = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: "", description: "", available_locations: "", expected_salary: "",
    skills: "", availability: "immediate"
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert(`❌ ${t('needLoginToOffer')}`);
        setLoading(false);
        return;
      }

      // Configure headers with token
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      // Check plan limits first
      const limitsResponse = await axios.get(`${API}/check-plan-limits/create_work_offer`, config);
      
      if (!limitsResponse.data.allowed) {
        alert(`❌ ${limitsResponse.data.message}\n\n${t('considerUpgrade')}`);
        setLoading(false);
        return;
      }

      await axios.post(`${API}/work-offers`, formData, config);
      setFormData({
        title: "", description: "", available_locations: "", expected_salary: "",
        skills: "", availability: "immediate"
      });
      onUpdate();
      alert(t('workOfferCreatedSuccess'));
    } catch (error) {
      console.error("Erro ao criar oferta:", error);
      alert(`${t('errorCreatingOffer')}: ` + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('createWorkOffer')}</CardTitle>
        <CardDescription>{t('offerServicesToEmployers')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">{t('offerTitle')}</Label>
              <Input 
                id="title" 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
                required 
              />
            </div>
            <div>
              <Label htmlFor="available_locations">{t('availableLocations')}</Label>
              <Input 
                id="available_locations" 
                value={formData.available_locations} 
                onChange={(e) => setFormData({...formData, available_locations: e.target.value})} 
                required 
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="expected_salary">{t('expectedSalary')}</Label>
              <Input 
                id="expected_salary" 
                value={formData.expected_salary} 
                onChange={(e) => setFormData({...formData, expected_salary: e.target.value})} 
                placeholder={t('expectedSalaryPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="availability">{t('availability')}</Label>
              <Select value={formData.availability} onValueChange={(value) => setFormData({...formData, availability: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">{t('immediate')}</SelectItem>
                  <SelectItem value="1-month">{t('oneMonth')}</SelectItem>
                  <SelectItem value="2-months">{t('twoMonths')}</SelectItem>
                  <SelectItem value="3-months">{t('threeMonths')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="description">{t('serviceDescription')}</Label>
            <Textarea 
              id="description" 
              value={formData.description} 
              onChange={(e) => setFormData({...formData, description: e.target.value})} 
              required 
              rows={4}
            />
          </div>
          <div>
            <Label htmlFor="skills">{t('skillsAndExperience')}</Label>
            <Textarea 
              id="skills" 
              value={formData.skills} 
              onChange={(e) => setFormData({...formData, skills: e.target.value})} 
              required 
              rows={3}
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? t('creating') : t('createOffer')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default WorkOfferForm;
